/**
 * Flora / fauna / tree models — 9 species.
 * Groundcover ported from design_sheets/flora_fauna.html; trees second pass (design_sheets/scenery.html).
 * All models built in local space (Y = up from surface). Deterministic: every random draw
 * goes through the seeded `rng` set per build, so peers grow identical plants.
 */

import * as THREE from 'three';
import { buildTree, type TreeColors } from './biomeKit';
import { TREE_FAMILIES, type TreeFamily } from './treeFamilies';
import { buildPiece } from './dressingPieces';
import { makeInstancedGroup, type InstancedGroup } from './meshBake';
import type { FloraType } from '../types';
import { mat as kitMat, cyl, torus, at } from './buildingKit';

/** The biome trees' colours when built outside a biome (the renderer colours biome stands itself). */
const KIT_NEUTRAL: TreeColors = { bark: '#5a4a3a', leaf: '#6f8a4c', leafDark: '#4e6638', accent: '#d6d2c4' };

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function mat(color: string): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ color: new THREE.Color(color) });
}

// Seeded xorshift; buildFloraModel points this at the flora's own seed.
let rng: () => number = Math.random;
function seededRng(seed: number): () => number {
  let s = (seed * 2654435761 + 12345) >>> 0 || 1;
  return () => { s ^= s << 13; s >>>= 0; s ^= s >>> 17; s ^= s << 5; s >>>= 0; return s / 4294967296; };
}
function pick<T>(arr: T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}

function rand(lo: number, hi: number): number {
  return lo + rng() * (hi - lo);
}

// ---------------------------------------------------------------------------
// FloraAnimData — returned alongside the Group for the animation system
// ---------------------------------------------------------------------------

export interface FloraAnimData {
  swayTargets: THREE.Object3D[];   // meshes or whole sub-groups (tiers, clusters, fronds)
  phases: number[];
  speeds: number[];
  baseRotations?: Array<{ x: number; z: number }>;
  // Lantern bracket specifics
  lights?: THREE.PointLight[];
  glowMeshes?: THREE.Mesh[];
  sporePoints?: THREE.Points;
  sporeDrifts?: Array<{ dx: number; dy: number; dz: number }>;
  // Poolskater specifics
  skaterStates?: Array<{
    state: 'paused' | 'darting';
    heading: number;
    speed: number;
    timer: number;
    duration: number;
    x: number;
    z: number;
    group: THREE.Group;
  }>;
  poolRadius?: number;
  // InstancedMesh groups to sync after the proxies have been animated
  instanced?: InstancedGroup[];
}

export function emptyAnimData(): FloraAnimData {
  return { swayTargets: [], phases: [], speeds: [] };
}

function pushSway(ad: FloraAnimData, target: THREE.Object3D): void {
  ad.swayTargets.push(target);
  ad.phases.push(rng() * Math.PI * 2);
  ad.speeds.push(0.8 + rng() * 0.6);
}

// ============================================================
// 1. Velvet Matgrass — ground disc + squashed sphere clumps
// ============================================================

function buildVelvetMatgrass(scale: number): { group: THREE.Group; animData: FloraAnimData } {
  const g = new THREE.Group();
  const ad = emptyAnimData();

  // Ground disc
  const disc = new THREE.Mesh(
    new THREE.CylinderGeometry(0.2 * scale, 0.2 * scale, 0.01, 12),
    mat('#2a3a2a')
  );
  disc.position.y = 0.005;
  g.add(disc);

  // Clumps
  const colors = ['#4a8a5a', '#55955a', '#3d7d4d', '#5a9a60', '#488a50'];
  for (let i = 0; i < 10; i++) {
    const r = rand(0.03, 0.06) * scale;
    const sphere = new THREE.Mesh(
      new THREE.SphereGeometry(r, 5, 3),
      mat(pick(colors))
    );
    const squash = rand(0.2, 0.3);
    sphere.scale.y = squash;
    const angle = rng() * Math.PI * 2;
    const dist = rng() * 0.15 * scale;
    sphere.position.set(
      Math.cos(angle) * dist,
      r * squash + 0.01,
      Math.sin(angle) * dist
    );
    g.add(sphere);
    pushSway(ad, sphere);
  }

  g.scale.setScalar(scale);
  return { group: g, animData: ad };
}

// ============================================================
// 2. Thermocap Lichen — rocks + shimmering lichen patches
// ============================================================

function buildThermocapLichen(scale: number): { group: THREE.Group; animData: FloraAnimData } {
  const g = new THREE.Group();
  const ad = emptyAnimData();

  // Rocks
  const rockColors = ['#4a3a30', '#3a3028'];
  for (let i = 0; i < 2; i++) {
    const rock = new THREE.Mesh(
      new THREE.IcosahedronGeometry(0.08, 1),
      mat(rockColors[i])
    );
    rock.scale.y = 0.5;
    rock.position.set(i === 0 ? -0.04 : 0.05, 0.03, i === 0 ? 0.02 : -0.03);
    rock.rotation.y = rng() * Math.PI;
    g.add(rock);
  }

  // Lichen patches
  const lichenColors = ['#b87a40', '#c89050', '#a06830', '#d0a060', '#c88a48'];
  for (let i = 0; i < 8; i++) {
    const r = rand(0.02, 0.04);
    const lichenMat = new THREE.MeshLambertMaterial({
      color: new THREE.Color(pick(lichenColors)),
      emissive: new THREE.Color(pick(lichenColors)),
      emissiveIntensity: 0.3,
    });
    const lichen = new THREE.Mesh(
      new THREE.CircleGeometry(r, 6),
      lichenMat
    );
    const angle = rng() * Math.PI * 2;
    const dist = rand(0.02, 0.1);
    lichen.position.set(
      Math.cos(angle) * dist,
      rand(0.02, 0.06),
      Math.sin(angle) * dist
    );
    lichen.rotation.x = -Math.PI / 2 + rand(-0.4, 0.4);
    lichen.rotation.z = rng() * Math.PI;
    g.add(lichen);
    pushSway(ad, lichen);
  }

  g.scale.setScalar(scale);
  return { group: g, animData: ad };
}

// ============================================================
// 3. Ghost Reed — frost patches + translucent reed stems
// ============================================================

function buildGhostReed(scale: number): { group: THREE.Group; animData: FloraAnimData } {
  const g = new THREE.Group();
  const ad = emptyAnimData();
  ad.baseRotations = [];

  // Frost patches on ground
  for (let i = 0; i < 4; i++) {
    const frost = new THREE.Mesh(
      new THREE.CircleGeometry(rand(0.03, 0.06), 6),
      mat('#c0c8d0')
    );
    frost.rotation.x = -Math.PI / 2;
    const angle = rng() * Math.PI * 2;
    const dist = rand(0.02, 0.1);
    frost.position.set(Math.cos(angle) * dist, 0.001, Math.sin(angle) * dist);
    g.add(frost);
  }

  // Reed stems
  const reedColors = ['#e8e0d8', '#d8d4cc', '#ece4dc', '#d0ccc4', '#e0dcd4'];
  for (let i = 0; i < 8; i++) {
    const topR = rand(0.004, 0.006);
    const h = rand(0.08, 0.2);
    const reedMat = new THREE.MeshLambertMaterial({
      color: new THREE.Color(pick(reedColors)),
      transparent: true,
      opacity: 0.85,
    });
    const reed = new THREE.Mesh(
      new THREE.CylinderGeometry(topR, topR * 1.2, h, 5, 1, true),
      reedMat
    );
    const angle = rng() * Math.PI * 2;
    const dist = rand(0.01, 0.1);
    reed.position.set(Math.cos(angle) * dist, h / 2, Math.sin(angle) * dist);
    g.add(reed);
    ad.swayTargets.push(reed);
    ad.phases.push(rng() * Math.PI * 2);
    ad.speeds.push(0.6 + rng() * 0.8);
    ad.baseRotations.push({ x: rand(-0.05, 0.05), z: rand(-0.05, 0.05) });
  }

  g.scale.setScalar(scale);
  return { group: g, animData: ad };
}

// ============================================================
// 4. Lantern Bracket — ridge + glowing half-circle brackets + spores
// ============================================================

function buildLanternBracket(scale: number): { group: THREE.Group; animData: FloraAnimData } {
  const g = new THREE.Group();
  const ad = emptyAnimData();
  ad.lights = [];
  ad.glowMeshes = [];

  // Ridge base
  const ridge = new THREE.Mesh(
    new THREE.BoxGeometry(0.05, 0.15, 0.1),
    mat('#2a2030')
  );
  ridge.position.y = 0.075;
  g.add(ridge);

  // Brackets
  for (let i = 0; i < 5; i++) {
    const r = rand(0.03, 0.04);
    const bracketGeo = new THREE.CircleGeometry(r, 8, 0, Math.PI);
    const bracket = new THREE.Mesh(bracketGeo, mat('#6a5878'));
    bracket.material.side = THREE.DoubleSide;

    const y = 0.03 + i * 0.025;
    const z = (i - 2) * 0.02;
    const x = 0.025 + rand(0, 0.01);
    bracket.position.set(x, y, z);
    bracket.rotation.y = Math.PI / 2;
    bracket.rotation.z = Math.PI / 2;
    g.add(bracket);

    // Glow underside
    // Lambert emissive (not MeshBasic) so the five glows bake into one draw; the
    // renderer pulses the merged material's emissive.
    const glowMat = new THREE.MeshLambertMaterial({
      color: new THREE.Color('#b090d0'), emissive: new THREE.Color('#b090d0'), emissiveIntensity: 0.6,
      transparent: true, opacity: 0.7, side: THREE.DoubleSide,
    });
    const glow = new THREE.Mesh(
      new THREE.CircleGeometry(r * 0.9, 8, 0, Math.PI),
      glowMat
    );
    glow.position.set(x - 0.002, y - 0.002, z);
    glow.rotation.y = Math.PI / 2;
    glow.rotation.z = Math.PI / 2;
    g.add(glow);
    ad.glowMeshes!.push(glow);

    pushSway(ad, bracket);
  }

  // No point light: emissive + bloom carries the glow (every point light in the
  // scene adds a loop iteration to every lit fragment).

  // Spore particles
  const sporeCount = 15;
  const sporePosArr = new Float32Array(sporeCount * 3);
  const sporeDrifts: Array<{ dx: number; dy: number; dz: number }> = [];
  for (let i = 0; i < sporeCount; i++) {
    const sx = rand(-0.06, 0.06);
    const sy = rand(0.02, 0.16);
    const sz = rand(-0.06, 0.06);
    sporePosArr[i * 3] = sx;
    sporePosArr[i * 3 + 1] = sy;
    sporePosArr[i * 3 + 2] = sz;
    sporeDrifts.push({
      dx: rand(-0.002, 0.002),
      dy: rand(0.001, 0.004),
      dz: rand(-0.002, 0.002),
    });
  }
  const sporeGeo = new THREE.BufferGeometry();
  sporeGeo.setAttribute('position', new THREE.BufferAttribute(sporePosArr, 3));
  const sporeMat = new THREE.PointsMaterial({
    color: 0xa080c0,
    size: 0.015,
    transparent: true,
    opacity: 0.4,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const sporePoints = new THREE.Points(sporeGeo, sporeMat);
  g.add(sporePoints);
  ad.sporePoints = sporePoints;
  ad.sporeDrifts = sporeDrifts;

  g.scale.setScalar(scale);
  return { group: g, animData: ad };
}

// ============================================================
// 5. Copperthread — water disc + thread tangles + mineral deposits
// ============================================================

function buildCopperthread(scale: number): { group: THREE.Group; animData: FloraAnimData } {
  const g = new THREE.Group();
  const ad = emptyAnimData();

  // Water disc
  const waterMat = new THREE.MeshBasicMaterial({
    color: new THREE.Color('#304848'),
    transparent: true,
    opacity: 0.6,
  });
  const water = new THREE.Mesh(
    new THREE.CylinderGeometry(0.2, 0.2, 0.005, 12),
    waterMat
  );
  water.position.y = 0.0025;
  g.add(water);

  // Thread tangles along one edge
  const threadColors = ['#408878', '#48a088', '#389070', '#50a890', '#3a8870'];
  for (let i = 0; i < 10; i++) {
    const r = rand(0.01, 0.03);
    const tangle = new THREE.Mesh(
      new THREE.SphereGeometry(r, 5, 3),
      mat(pick(threadColors))
    );
    tangle.scale.y = 0.3;
    // Scatter along one edge of the pool
    const angle = rand(-0.5, 0.5);
    const dist = rand(0.12, 0.19);
    tangle.position.set(
      Math.cos(angle) * dist,
      r * 0.3 + 0.005,
      Math.sin(angle) * dist
    );
    g.add(tangle);
    pushSway(ad, tangle);
  }

  // Mineral deposits
  const mineralMat = new THREE.MeshBasicMaterial({
    color: new THREE.Color('#60c0a0'),
    transparent: true,
    opacity: 0.5,
  });
  for (let i = 0; i < 4; i++) {
    const mineral = new THREE.Mesh(
      new THREE.SphereGeometry(0.008, 5, 3),
      mineralMat.clone()
    );
    const angle = rand(0, Math.PI * 2);
    const dist = rand(0.05, 0.15);
    mineral.position.set(
      Math.cos(angle) * dist,
      0.008,
      Math.sin(angle) * dist
    );
    g.add(mineral);
  }

  g.scale.setScalar(scale);
  return { group: g, animData: ad };
}

// ============================================================
// 6–8. Spire pine, canopy oak, frond palm — 70.2: the redrawn families (treeFamilies.ts), built
//      below with the kit's seven (the same trees at both sizes: the sim measures one model)
// ============================================================

// ============================================================
// 9. Poolskater — dark pool + rim + darting insect skaters
// ============================================================

function buildPoolskater(scale: number): { group: THREE.Group; animData: FloraAnimData } {
  const g = new THREE.Group();
  const ad = emptyAnimData();
  const poolR = 0.2;

  // Pool water
  const poolMat = new THREE.MeshBasicMaterial({
    color: new THREE.Color('#283838'),
    transparent: true,
    opacity: 0.7,
  });
  const pool = new THREE.Mesh(
    new THREE.CylinderGeometry(poolR, poolR, 0.008, 16),
    poolMat
  );
  pool.position.y = 0.004;
  g.add(pool);

  // Rim
  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(poolR, 0.01, 6, 16),
    mat('#3a4040')
  );
  rim.rotation.x = Math.PI / 2;
  rim.position.y = 0.008;
  g.add(rim);

  // Skaters — 5 bodies + 20 legs as two InstancedMeshes driven by proxy groups
  ad.skaterStates = [];
  ad.poolRadius = poolR;

  const bodyGeo = new THREE.CylinderGeometry(0.01, 0.01, 0.003, 6);
  const bodyMat = mat('#607878');
  const legGeo = new THREE.CylinderGeometry(0.001, 0.001, 0.025, 4);
  const legMat = mat('#708888');
  const bodyProxies: THREE.Object3D[] = [];
  const legProxies: THREE.Object3D[] = [];

  for (let i = 0; i < 5; i++) {
    const skaterGroup = new THREE.Group();

    const body = new THREE.Object3D();
    body.position.y = 0.003;
    skaterGroup.add(body);
    bodyProxies.push(body);

    for (let j = 0; j < 4; j++) {
      const legAngle = (j / 4) * Math.PI * 2 + Math.PI / 4;
      const leg = new THREE.Object3D();
      leg.position.set(
        Math.cos(legAngle) * 0.012,
        0.002,
        Math.sin(legAngle) * 0.012
      );
      leg.rotation.z = Math.cos(legAngle) * 0.6;
      leg.rotation.x = Math.sin(legAngle) * 0.6;
      skaterGroup.add(leg);
      legProxies.push(leg);
    }

    // Random initial position within pool
    const initAngle = rng() * Math.PI * 2;
    const initDist = rng() * (poolR - 0.03);
    const sx = Math.cos(initAngle) * initDist;
    const sz = Math.sin(initAngle) * initDist;
    skaterGroup.position.set(sx, 0.01, sz);
    g.add(skaterGroup);

    ad.skaterStates.push({
      state: 'paused',
      heading: rng() * Math.PI * 2,
      speed: 0,
      timer: rng() * 2,
      duration: rand(0.5, 2.0),
      x: sx,
      z: sz,
      group: skaterGroup,
    });
  }

  ad.instanced = [
    makeInstancedGroup(g, bodyGeo, bodyMat, bodyProxies),
    makeInstancedGroup(g, legGeo, legMat, legProxies),
  ];

  g.scale.setScalar(scale);
  return { group: g, animData: ad };
}


// ============================================================
// Dispatcher
// ============================================================

const FLORA_BUILDERS: Record<FloraType, (scale: number) => { group: THREE.Group; animData: FloraAnimData }> = {
  velvetMatgrass: buildVelvetMatgrass,
  thermocapLichen: buildThermocapLichen,
  ghostReed: buildGhostReed,
  lanternBracket: buildLanternBracket,
  copperthread: buildCopperthread,
  poolskater: buildPoolskater,
  // E5 biome trees and (70.2) the classic three: the families in a neutral broadleaf palette (the renderer colours biome
  // stands itself; the spire pine, oak and palm keep their own colours)
  ...Object.fromEntries(TREE_FAMILIES.map(sp => [sp, (scale: number) => {
    const t = buildTree(sp, KIT_NEUTRAL, scale, Math.floor(rng() * 1e6));
    const animData = emptyAnimData();
    for (const o of t.sway) pushSway(animData, o);
    return { group: t.group, animData };
  }])) as Record<TreeFamily, (scale: number) => { group: THREE.Group; animData: FloraAnimData }>,
  // 74.4: the dressing pieces the sim holds as flora (the shrub, the log), in the broadleaf's colours
  ...Object.fromEntries((['shrub', 'log'] as const).map(k => [k, (scale: number) => {
    const g = buildPiece(k, 'broadleaf', Math.floor(rng() * 1e6));
    g.scale.setScalar(scale);
    return { group: g, animData: emptyAnimData() };
  }])) as Record<'shrub' | 'log', (scale: number) => { group: THREE.Group; animData: FloraAnimData }>,
};

export function buildFloraModel(floraType: FloraType, scale: number, seed = 1): { group: THREE.Group; animData: FloraAnimData } {
  rng = seededRng(seed);
  try { return FLORA_BUILDERS[floraType](scale); } finally { rng = Math.random; }
}
